<template>
  <div>
    <CCard>
      <CCardHeader>
        <strong>Создание A/B теста</strong>
      </CCardHeader>
      <CCardBody>
        <CForm @submit.prevent="createTest">
          <CRow>
            <CCol md="6">
              <CInput
                label="Название теста"
                v-model="test.name"
                :invalid="!!errors.name"
                :feedback="errors.name"
                required
              />
            </CCol>
            <CCol md="6">
              <CInput
                label="Ключ теста"
                v-model="test.key"
                :invalid="!!errors.key"
                :feedback="errors.key"
                description="Уникальный ключ для использования в коде приложения"
                required
              />
            </CCol>
          </CRow>
          
          <CRow>
            <CCol md="12">
              <CTextarea
                label="Описание"
                v-model="test.description"
                rows="3"
                :invalid="!!errors.description"
                :feedback="errors.description"
              />
            </CCol>
          </CRow>
          
          <CRow>
            <CCol md="6">
              <CInput
                label="Процент трафика"
                v-model.number="test.traffic_allocation"
                type="number"
                min="1"
                max="100"
                :invalid="!!errors.traffic_allocation"
                :feedback="errors.traffic_allocation"
                description="Процент пользователей, участвующих в тесте (1-100%)"
                required
              />
            </CCol>
            <CCol md="6">
              <div class="form-group">
                <label class="form-label">
                  <input 
                    type="checkbox" 
                    v-model="test.active"
                    class="mr-2"
                  />
                  Активный тест
                </label>
                <div class="small text-muted">
                  Активный: {{ test.active ? 'Да' : 'Нет' }}
                </div>
              </div>
            </CCol>
          </CRow>
          
          <hr>
          
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h5>Варианты теста</h5>
            <CButton
              color="secondary"
              variant="outline"
              size="sm"
              @click="addVariant"
            >
              <CIcon name="cil-plus" class="mr-1" />
              Добавить вариант
            </CButton>
          </div>
          
          <div v-if="test.variants.length === 0" class="text-muted text-center py-3">
            Добавьте варианты для A/B теста
          </div>
          
          <CRow v-for="(variant, index) in test.variants" :key="index" class="mb-3">
            <CCol md="3">
              <CInput
                :label="`Название варианта ${index + 1}`"
                v-model="variant.name"
                required
              />
            </CCol>
            <CCol md="3">
              <CInput
                :label="`Ключ варианта ${index + 1}`"
                v-model="variant.key"
                required
              />
            </CCol>
            <CCol md="2">
              <CInput
                :label="`Вес ${index + 1}`"
                v-model.number="variant.weight"
                type="number"
                min="1"
                max="100"
                required
              />
            </CCol>
            <CCol md="3">
              <CTextarea
                :label="`Конфигурация JSON ${index + 1}`"
                v-model="variant.configString"
                rows="2"
                placeholder='{"key": "value"}'
              />
            </CCol>
            <CCol md="1" class="d-flex align-items-end">
              <CButton
                color="danger"
                variant="outline"
                size="sm"
                @click="removeVariant(index)"
                class="mb-3"
              >
                <CIcon name="cil-trash" />
              </CButton>
            </CCol>
          </CRow>
          
          <div v-if="test.variants.length > 0" class="alert alert-info">
            <strong>Общий вес:</strong> {{ totalWeight }}%
            <span v-if="totalWeight !== 100" class="text-warning ml-2">
              (рекомендуется 100%)
            </span>
          </div>
          
          <hr>
          
          <div class="d-flex justify-content-between">
            <CButton
              color="secondary"
              variant="outline"
              @click="$router.push('/ab-tests')"
            >
              Отмена
            </CButton>
            <CButton
              color="primary"
              type="submit"
              :disabled="loading || test.variants.length === 0"
            >
              <CSpinner v-if="loading" size="sm" class="mr-1" />
              {{ loading ? 'Создание...' : 'Создать тест' }}
            </CButton>
          </div>
        </CForm>
      </CCardBody>
    </CCard>
  </div>
</template>

<script>
export default {
  name: 'AbTestCreate',
  data() {
    return {
      loading: false,
      errors: {},
      test: {
        name: '',
        key: '',
        description: '',
        traffic_allocation: 100,
        active: false,
        variants: []
      }
    }
  },
  computed: {
    totalWeight() {
      return this.test.variants.reduce((sum, variant) => sum + (variant.weight || 0), 0)
    }
  },
  methods: {
    addVariant() {
      this.test.variants.push({
        name: '',
        key: '',
        weight: 50,
        configString: ''
      })
    },
    
    removeVariant(index) {
      this.test.variants.splice(index, 1)
    },
    
    async createTest() {
      this.loading = true
      this.errors = {}
      
      try {
        // Валидация
        if (!this.test.name) {
          this.errors.name = 'Название обязательно'
        }
        if (!this.test.key) {
          this.errors.key = 'Ключ обязателен'
        }
        if (this.test.variants.length === 0) {
          this.$root.$emit('showError', 'Добавьте хотя бы один вариант теста')
          return
        }
        
        // Проверяем варианты
        for (const variant of this.test.variants) {
          if (!variant.name || !variant.key || !variant.weight) {
            this.$root.$emit('showError', 'Заполните все поля вариантов')
            return
          }
          
          // Парсим JSON конфигурацию
          if (variant.configString) {
            try {
              variant.config = JSON.parse(variant.configString)
            } catch (e) {
              this.$root.$emit('showError', `Некорректный JSON в варианте "${variant.name}"`)
              return
            }
          } else {
            variant.config = {}
          }
        }
        
        if (Object.keys(this.errors).length > 0) {
          return
        }
        
        // Подготавливаем данные для отправки
        const testData = { ...this.test }
        testData.variants = testData.variants.map(v => ({
          name: v.name,
          key: v.key,
          weight: v.weight,
          config: v.config
        }))
        
        console.log('Original test object:', this.test)
        console.log('Sending test data:', testData)
        const { data } = await this.$http.post('/ab-tests', testData)
        console.log('Response data:', data)
        this.$root.$emit('showSuccess', `A/B тест "${data.name}" успешно создан`)
        
        // Используем объект с path как в других компонентах

        console.log('data.id', data.id);
        console.log('Redirecting to path:', '/ab-tests/' + data.id);
        
        await this.$router.push({
          path: '/ab-tests/' + data.id
        })
        
        console.log('Redirect completed')
              } catch (error) {
          console.error('Full error object:', error)
          if (error.response && error.response.data && error.response.data.message) {
            this.$root.$emit('showError', error.response.data.message)
          } else if (error.message) {
            this.$root.$emit('showError', 'Ошибка: ' + error.message)
          } else {
            this.$root.$emit('showError', 'Ошибка создания теста')
          }
          console.error('Error details:', error)
        } finally {
          this.loading = false
        }
    }
  }
}
</script> 